import React from "react"
import * as RechartsPrimitive from "recharts"
import { ChartContext, type ChartConfig } from "./chartBase.context"
import ChartStyle from "./ChartBaseStyle"

const ChartContainer = ({
  id,
  className,
  children,
  config,
  ...props
}: React.ComponentProps<"div"> & {
  config: ChartConfig
}) => {
  const uniqueId = React.useId()
  const chartId = `chart-${id || uniqueId.replaceAll(":", "")}`

  // Measure the container so ResponsiveContainer gets real pixel dimensions
  // via initialDimension, suppressing its "-1" sentinel "width/height must be
  // > 0" warning on first paint.
  const containerRef = React.useRef<HTMLDivElement>(null)
  const [initialDimension, setInitialDimension] = React.useState<
    { width: number; height: number } | undefined
  >()
  React.useEffect(() => {
    const el = containerRef.current
    if (!el) return
    const observer = new ResizeObserver(([entry]) => {
      const { width, height } = entry.contentRect
      if (width > 0 && height > 0) {
        setInitialDimension({ width, height })
        observer.disconnect()
      }
    })
    observer.observe(el)
    return () => observer.disconnect()
  }, [])

  return (
    <ChartContext value={{ config }}>
      <div
        ref={containerRef}
        data-chart={chartId}
        className={className}
        {...props}
      >
        <ChartStyle id={chartId} config={config} />
        {initialDimension ? (
          <RechartsPrimitive.ResponsiveContainer
            initialDimension={initialDimension}
          >
            {children}
          </RechartsPrimitive.ResponsiveContainer>
        ) : null}
      </div>
    </ChartContext>
  )
}
ChartContainer.displayName = "Chart"

export default ChartContainer
